/**
 * T00VOQ5DCZSJ — übernommen aus simpleforyou-web (T00CJYJVSDBK): das
 * GoCardless-Drop-in als Overlay über unserer Seite.
 *
 * Die Bankverbindung wird ausschliesslich in diesem Overlay erfasst — es
 * gehört GoCardless, spricht direkt mit GoCardless, und keine IBAN berührt
 * unsere Anwendung. Deshalb das fremde Skript: der Preis dafür, dass wir die
 * Daten nie sehen.
 *
 * Der Flow selbst wird serverseitig angelegt; hierher kommt nur seine Id. Die
 * Umgebung kommt ebenfalls vom Server, damit sie nicht an zwei Stellen
 * entschieden wird — ein Live-Flow, der im Sandbox-Drop-in geöffnet wird, ist
 * schlicht nicht auffindbar.
 *
 * `onSuccess` ist KEIN Beweis. Es sagt dem Aufrufer nur, dass er den Server
 * nachsehen lassen soll; geschrieben wird ausschliesslich dort.
 */

const DROPIN_SCRIPT_URL = 'https://pay.gocardless.com/billing/static/dropin/v2/initialise.js';

export type GoCardlessDropinOptions = {
  billingRequestFlowID: string;
  environment: string;
  onSuccess: () => void;
  onExit?: (error?: unknown) => void;
};

type DropinHandler = {open: () => void};
type DropinGlobal = {create: (options: Record<string, unknown>) => DropinHandler};

declare global {
  interface Window {
    GoCardlessDropin?: DropinGlobal;
  }
}

let scriptPromise: Promise<DropinGlobal> | null = null;

/**
 * Lädt das Drop-in-Skript genau einmal je Seitenaufruf.
 *
 * Ein zweiter Versuch (Kunde bricht ab und startet neu) darf kein zweites
 * <script> in den Kopf hängen; die gemerkte Zusage wird wiederverwendet. Nur
 * ein FEHLGESCHLAGENER Ladevorgang wird vergessen, damit ein Netzwerkaussetzer
 * die Seite nicht dauerhaft lähmt.
 */
export const loadGoCardlessDropin = (): Promise<DropinGlobal> => {
  if (window.GoCardlessDropin) {
    return Promise.resolve(window.GoCardlessDropin);
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<DropinGlobal>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = DROPIN_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.GoCardlessDropin) {
        resolve(window.GoCardlessDropin);
      } else {
        reject(new Error('GoCardless drop-in loaded without exposing GoCardlessDropin'));
      }
    };
    script.onerror = () => reject(new Error(`Could not load ${DROPIN_SCRIPT_URL}`));
    document.head.appendChild(script);
  }).catch((error) => {
    scriptPromise = null;
    throw error;
  });

  return scriptPromise;
};

/** Nur für Tests: den gemerkten Ladezustand vergessen. */
export const resetGoCardlessDropin = () => {
  scriptPromise = null;
  delete window.GoCardlessDropin;
};

export function useGoCardlessDropin() {

  const open = async (options: GoCardlessDropinOptions): Promise<void> => {
    const dropin = await loadGoCardlessDropin();
    dropin.create({
      billingRequestFlowID: options.billingRequestFlowID,
      environment: options.environment,
      onSuccess: options.onSuccess,
      onExit: options.onExit,
    }).open();
  };

  return {
    open,
  };
}
